import React from 'react';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { getImageUrl } from 'helpers/Image.helper';
import LoadingComponent from 'commons/loading/Loading.component';

interface GroupedCastingLog {
    month: string; // YYYY-MM
    agencies: {
        agencyId: number;
        agencyName: string;
        logoImage: any;
        castingCount: number;
    }[];
}

interface CastingIndexComponentProps {
    groupedData: GroupedCastingLog[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

const CastingIndexComponent: React.FC<CastingIndexComponentProps> = ({
    groupedData,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore
}) => {
    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubCasting.txtTitle")}
                </h1>

                <div className="max-w-3xl mx-auto">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingComponent />
                        </div>
                    ) : groupedData.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-sm">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubCasting.txtNoData")}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {groupedData.map((group, groupIndex) => (
                                <div key={groupIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
                                    {/* 월 헤더 */}
                                    <div className="px-4 py-2 border-b">
                                        <h2 className="text-base font-bold text-gray-900">
                                            {group.month}
                                        </h2>
                                    </div>

                                    {/* 기획사 목록 */}
                                    <div className="divide-y divide-gray-100">
                                        {group.agencies.map((agency, agencyIndex) => (
                                            <div 
                                                key={agencyIndex} 
                                                className="px-4 py-3 hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        {/* 기획사 로고 */}
                                                        <div className="w-10 h-10 rounded overflow-hidden border border-gray-200 flex-shrink-0">
                                                            <img 
                                                                src={getImageUrl(agency.logoImage, 100)} 
                                                                alt={agency.agencyName}
                                                                className="w-full h-full object-contain"
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).src = './imgs/icons/icon-agency-profile.svg';
                                                                }}
                                                            />
                                                        </div>

                                                        {/* 기획사 이름 */}
                                                        <div>
                                                            <h3 className="text-sm font-bold text-gray-900">
                                                                {agency.agencyName}
                                                            </h3>
                                                        </div>
                                                    </div>

                                                    {/* 캐스팅 횟수 */}
                                                    <div className="text-right">
                                                        <div className="text-xl font-bold text-[#CF3688]">
                                                            {agency.castingCount}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* 더보기 버튼 */}
                            {hasMore && !isLoadingMore && (
                                <div className="flex justify-center py-4">
                                    <button
                                        onClick={onLoadMore}
                                        className="px-5 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg
                                            hover:bg-gray-50 transition-colors"
                                    >
                                        {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtMore")}
                                    </button>
                                </div>
                            )}

                            {/* 추가 로딩 인디케이터 */}
                            {isLoadingMore && (
                                <div className="flex justify-center items-center py-6">
                                    <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            )}

                            {/* 더 이상 데이터가 없을 때 */}
                            {!hasMore && groupedData.length > 0 && !isLoadingMore && (
                                <div className="flex justify-center items-center py-6">
                                    <div className="text-center text-gray-400 text-xs">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubCasting.txtNoMoreData")}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default CastingIndexComponent;

